// useTokenWatcher — Polls /api/shadow/tokens/balance for the logged-in user
// every 20s and toasts a mini "🪙 +N $REBEL" notification when the balance
// increases. Renders nothing; mount once near the app root.
//
// Skips toasting on the very first poll (otherwise users see a phantom
// "+balance" notification on every page load). Also tolerates short network
// drops gracefully.
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const POLL_MS = 20_000;

export const useTokenWatcher = () => {
  const { user, token } = useAuth();
  const lastBalRef = useRef(null);

  useEffect(() => {
    if (!user || !token) {
      lastBalRef.current = null;
      return;
    }
    let mounted = true;
    let timer = null;

    const poll = async () => {
      try {
        const r = await fetch(`${API}/shadow/tokens/balance`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const j = await r.json();
        const bal = Number(j?.balance || 0);
        if (!mounted) return;

        const prev = lastBalRef.current;
        if (prev != null && bal > prev) {
          const delta = bal - prev;
          toast(`🪙 +${delta} $REBEL`, {
            description: `Balance: ${bal.toLocaleString()} R₿L`,
            duration: 4000,
            position: "bottom-right",
          });
        }
        lastBalRef.current = bal;
      } catch (_e) {
        /* non-critical */
      }
    };

    // Prime the cache silently — do NOT toast for the initial value.
    poll();
    timer = setInterval(poll, POLL_MS);
    return () => {
      mounted = false;
      if (timer) clearInterval(timer);
    };
  }, [user, token]);
};

// Standalone component wrapper so callers can mount it declaratively
// alongside <Toaster /> without writing a custom component.
export const TokenWatcher = () => {
  useTokenWatcher();
  return null;
};

export default useTokenWatcher;
